import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select } from '../components/ui/select';
import { useToast } from '../components/ui/use-toast';
import { 
  ArrowLeft, MapPin, Calendar, User, CheckCircle, Clock, AlertTriangle,
  Camera, Upload, Loader2, X, ChevronLeft, ChevronRight, MessageSquare,
  RefreshCw, LogOut, Smartphone
} from 'lucide-react';

// Helper to get status config
const getStatusConfig = (estado) => {
  if (!estado) return { color: 'bg-gray-500', label: 'Sin Estado', icon: Clock };
  const estadoLower = estado.toLowerCase();
  if (estadoLower.includes('finalizada') || estadoLower.includes('completada')) 
    return { color: 'bg-green-600', label: 'Completada', icon: CheckCircle };
  if (estadoLower.includes('ejecucion') || estadoLower.includes('progreso')) 
    return { color: 'bg-orange-500', label: 'En Progreso', icon: Clock };
  if (estadoLower.includes('pendiente')) 
    return { color: 'bg-gray-400', label: 'Pendiente', icon: Clock };
  if (estadoLower.includes('rechazada') || estadoLower.includes('bloqueada')) 
    return { color: 'bg-red-600', label: 'Bloqueada', icon: AlertTriangle };
  return { color: 'bg-gray-500', label: estado, icon: Clock };
};

// Convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = (error) => reject(error);
  });
};

// Task List Item Component
function TaskItem({ task, onClick }) {
  const statusConfig = getStatusConfig(task.estado);
  const StatusIcon = statusConfig.icon;
  
  return (
    <Card 
      className="mb-3 cursor-pointer hover:shadow-md transition-shadow active:bg-muted/50"
      onClick={() => onClick(task)}
      data-testid={`mobile-task-${task.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-full ${statusConfig.color} flex items-center justify-center flex-shrink-0`}>
            <StatusIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                {task.codigo}
              </span>
              <Badge className={`${statusConfig.color} text-white text-[10px]`}>
                {statusConfig.label}
              </Badge>
            </div>
            <h3 className="font-medium text-sm line-clamp-2">
              {task.descripcion || task.titulo || 'Sin título'}
            </h3>
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <MapPin className="w-3 h-3" />
              <span className="truncate">{task.direccion || 'Sin dirección'}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Task Detail Component
function TaskDetail({ task, onBack, onUpdate }) {
  const [comment, setComment] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadPreviews, setUploadPreviews] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageDataUrls, setImageDataUrls] = useState({});
  const [loadingImageIds, setLoadingImageIds] = useState(new Set());
  const fileInputRef = useRef(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch images list
  const { data: imagesData, isLoading: imagesLoading, refetch: refetchImages } = useQuery({
    queryKey: ['mobile-task-images', task.id],
    queryFn: async () => {
      const response = await api.get(`/tasks/${task.id}/images`);
      return response.data;
    },
  });

  const images = imagesData?.images || [];
  const statusConfig = getStatusConfig(task.estado);

  // Fetch base64 image data for current image
  const fetchImageData = useCallback(async (imageId, taskId) => {
    setLoadingImageIds(prev => {
      if (prev.has(imageId)) return prev;
      return new Set(prev).add(imageId);
    });
    try {
      const response = await api.get(`/tasks/${taskId}/images/${imageId}/download`);
      const dataUrl = response.data?.dataUrl;
      if (dataUrl && dataUrl.startsWith('data:image/')) {
        setImageDataUrls(prev => ({ ...prev, [imageId]: dataUrl }));
      }
    } catch (error) {
      console.error(`Error fetching image ${imageId}:`, error);
    } finally {
      setLoadingImageIds(prev => {
        const s = new Set(prev);
        s.delete(imageId);
        return s;
      });
    }
  }, []);

  // Fetch image when index changes
  useEffect(() => {
    if (images.length > 0 && images[currentImageIndex]) {
      const img = images[currentImageIndex];
      if (img?.id && !imageDataUrls[img.id]) {
        fetchImageData(img.id, task.id);
      }
    }
  }, [currentImageIndex, images, imageDataUrls, fetchImageData, task.id]);

  // Update status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put(`/tasks/${task.id}/status`, data);
      return response.data;
    },
    onSuccess: () => {
      toast({ title: 'Estado actualizado' });
      queryClient.invalidateQueries({ queryKey: ['mobile-my-tasks'] });
      onUpdate();
    },
    onError: () => {
      toast({ title: 'Error al actualizar', variant: 'destructive' });
    },
  });

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.post(`/tasks/${task.id}/comments`, data);
      return response.data;
    },
    onSuccess: () => {
      toast({ title: 'Comentario agregado' });
      setComment('');
    },
    onError: () => {
      toast({ title: 'Error al agregar comentario', variant: 'destructive' });
    },
  });

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      const maxSize = 10 * 1024 * 1024;
      return validTypes.includes(file.type) && file.size <= maxSize;
    });

    const newPreviews = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setSelectedFiles(prev => [...prev, ...validFiles]);
    setUploadPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeSelectedFile = (index) => {
    URL.revokeObjectURL(uploadPreviews[index].preview);
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) return;
    setIsUploading(true);

    for (const file of selectedFiles) {
      try {
        const base64 = await fileToBase64(file);
        const extension = file.name.split('.').pop().toLowerCase();
        await api.post(`/tasks/${task.id}/images`, {
          nombre: file.name,
          extension,
          base64,
          tipo: 'I',
          momento_subida: 'antes-finalizacion'
        });
      } catch (error) {
        console.error('Error uploading:', error);
      }
    }

    setIsUploading(false);
    uploadPreviews.forEach(p => URL.revokeObjectURL(p.preview));
    setSelectedFiles([]);
    setUploadPreviews([]);
    toast({ title: 'Imágenes subidas' });
    refetchImages();
  };

  const handleUpdateStatus = () => {
    if (!newStatus) return;
    updateStatusMutation.mutate({
      estado_id: parseInt(newStatus),
      comentario: comment || null
    });
  };

  const handleAddComment = () => {
    if (!comment.trim()) return;
    addCommentMutation.mutate({ comentario: comment });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#008040] text-white p-4 shadow-md">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} className="text-white hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <span className="text-xs opacity-80 font-mono">{task.codigo}</span>
            <h1 className="font-semibold truncate">{task.descripcion || 'Sin título'}</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 pb-24">
        {/* Status Badge */}
        <div className="flex items-center gap-2">
          <Badge className={`${statusConfig.color} text-white`}>{statusConfig.label}</Badge>
          <Badge variant="outline">Prioridad: {task.prioridad}</Badge>
        </div>

        {/* Description */}
        {task.contenido && (
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold text-sm mb-2">Descripción</h3>
              <p className="text-sm text-muted-foreground">{task.contenido}</p>
            </CardContent>
          </Card>
        )}

        {/* Location */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 text-muted-foreground" />
              <div>
                <h3 className="font-semibold text-sm">Ubicación</h3>
                <p className="text-sm text-muted-foreground">{task.direccion}</p>
                {(task.barrio || task.distrito) && (
                  <p className="text-xs text-muted-foreground">{task.barrio} - {task.distrito}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Images */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Imágenes ({images.length})
            </h3>

            {imagesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : images.length > 0 ? (
              <div className="relative">
                <div className="aspect-video bg-muted rounded-md overflow-hidden flex items-center justify-center">
                  {loadingImageIds.has(images[currentImageIndex]?.id) ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">Cargando imagen...</span>
                    </div>
                  ) : imageDataUrls[images[currentImageIndex]?.id] ? (
                    <img
                      src={imageDataUrls[images[currentImageIndex]?.id]}
                      alt={`Imagen ${currentImageIndex + 1}`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 p-4 text-center">
                      <AlertTriangle className="w-8 h-8 text-amber-500" />
                      <span className="text-xs text-muted-foreground">No se pudo cargar la imagen</span>
                    </div>
                  )}
                </div>
                {images.length > 1 && (
                  <div className="flex items-center justify-between mt-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentImageIndex(i => (i - 1 + images.length) % images.length)}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs">{currentImageIndex + 1} / {images.length}</span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setCurrentImageIndex(i => (i + 1) % images.length)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">No hay imágenes</p>
            )}

            {/* Upload */}
            <div className="mt-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <Button
                variant="outline"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                <Camera className="w-4 h-4 mr-2" />
                Tomar / Seleccionar Foto
              </Button>

              {uploadPreviews.length > 0 && (
                <div className="mt-3 space-y-2">
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {uploadPreviews.map((preview, index) => (
                      <div key={index} className="relative flex-shrink-0">
                        <img
                          src={preview.preview}
                          alt={preview.name}
                          className="w-16 h-16 object-cover rounded-md border"
                        />
                        <button
                          onClick={() => removeSelectedFile(index)}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button
                    className="w-full"
                    onClick={handleUploadImages}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Subiendo...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" />Subir {uploadPreviews.length} foto(s)</>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Update Status */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3">Actualizar Estado</h3>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="mb-3"
            >
              <option value="">Seleccionar estado...</option>
              <option value="17">En Reparación</option>
              <option value="18">Completada</option>
              <option value="19">Bloqueada</option>
            </Select>
            <Textarea
              placeholder="Comentario (opcional)..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="mb-3"
            />
            <Button
              className="w-full bg-[#008040] hover:bg-[#006030]"
              onClick={handleUpdateStatus}
              disabled={!newStatus || updateStatusMutation.isPending}
            >
              {updateStatusMutation.isPending ? 'Actualizando...' : 'Actualizar Estado'}
            </Button>
          </CardContent>
        </Card>

        {/* Add Comment Only */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Agregar Solo Comentario
            </h3>
            <Textarea
              placeholder="Escribe un comentario..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              className="mb-3"
            />
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleAddComment}
              disabled={!comment.trim() || addCommentMutation.isPending}
            >
              {addCommentMutation.isPending ? 'Enviando...' : 'Enviar Comentario'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// Login Required Component
function LoginRequired() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-sm">
        <CardContent className="p-6 text-center">
          <Smartphone className="w-12 h-12 mx-auto mb-4 text-[#008040]" />
          <h2 className="font-semibold mb-4 text-lg">Acceso Móvil Encargados</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Debes iniciar sesión para acceder a tus incidencias asignadas
          </p>
          <Button 
            onClick={() => navigate('/login?redirect=/mobile')} 
            className="w-full bg-[#008040] hover:bg-[#006030]"
          >
            Iniciar Sesión
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// Main Mobile Task List Component
function MobileTaskList({ user, onSelectTask, onLogout }) {
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  // Fetch my tasks - this hook is now unconditional
  const { data: tasksData, isLoading, refetch } = useQuery({
    queryKey: ['mobile-my-tasks', statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      const response = await api.get(`/tasks/my-tasks?${params}`);
      return response.data;
    },
  });

  const tasks = tasksData?.data || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#008040] text-white p-4 shadow-md">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-bold text-lg">Mis Incidencias</h1>
            <p className="text-sm opacity-80">{user?.nombre} {user?.apellido}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => refetch()}
              className="text-white hover:bg-white/20"
            >
              <RefreshCw className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onLogout}
              className="text-white hover:bg-white/20"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="p-4 bg-white dark:bg-gray-800 border-b">
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full"
        >
          <option value="">Todas las incidencias</option>
          <option value="estados.pendiente_entidad_corto">Pendientes</option>
          <option value="estados.en_ejecucion">En Ejecución</option>
          <option value="estados.reparacion.finalizada">Finalizadas</option>
        </Select>
      </div>

      {/* Task List */}
      <div className="p-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#008040]" />
          </div>
        ) : tasks.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="font-semibold mb-2">No hay incidencias</h3>
              <p className="text-sm text-muted-foreground">
                No tienes incidencias asignadas con los filtros seleccionados
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-3">
              {tasks.length} incidencia(s) encontrada(s)
            </p>
            {tasks.map(task => (
              <TaskItem 
                key={task.id} 
                task={task} 
                onClick={onSelectTask}
              />
            ))}
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t p-2 safe-area-pb">
        <Button 
          className="w-full"
          variant="outline"
          onClick={() => navigate('/')}
        >
          Ir al Panel Completo
        </Button>
      </div>
    </div>
  );
}

// Main Mobile Page - Wrapper to handle auth
export default function MobileEncargadoPage() {
  const [selectedTask, setSelectedTask] = useState(null);
  const navigate = useNavigate();

  // Get token and user from localStorage
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  const userStr = localStorage.getItem('user_data') || localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login?redirect=/mobile');
  };

  // If not logged in, show login required
  if (!token || !user) {
    return <LoginRequired />;
  }

  // If task is selected, show detail
  if (selectedTask) {
    return (
      <TaskDetail 
        task={selectedTask} 
        onBack={() => setSelectedTask(null)}
        onUpdate={() => setSelectedTask(null)}
      />
    );
  }

  // Show task list
  return (
    <MobileTaskList 
      user={user}
      onSelectTask={setSelectedTask}
      onLogout={handleLogout}
    />
  );
}
